// Static curated content for the Help Center.
// Each entry is structured so the Knowledge Base search can index it.

export const BUSINESS_GUIDES = [
  {
    id: "add-lead",
    title: "How to Add a Lead",
    where: "Most leads arrive automatically from the public request form. To add one manually, see the Manual Entry Center.",
    steps: [
      "Open the public site (Public site link in the header).",
      "Submit the Request Service form on behalf of the caller, OR use the Manual Entry Center.",
      "Confirm the new lead appears in Leads with status 'new'.",
      "Add notes and set priority from the lead detail dialog.",
    ],
  },
  {
    id: "add-client",
    title: "How to Add a Client",
    where: "Clients module — derived directory of all consumer + facility records.",
    steps: [
      "Every lead and facility becomes a client automatically.",
      "Open Clients to search across both pipelines in one place.",
      "Use the kind chip to filter Consumers vs Facilities.",
    ],
  },
  {
    id: "add-facility",
    title: "How to Add a Facility",
    where: "Facilities & Providers module. Facilities mostly come in via the public partner form.",
    steps: [
      "For inbound leads: facility submits the partner form on the public site.",
      "For outbound prospecting: use Manual Entry Center → Facility.",
      "From the Facilities panel, set status, schedule call, send quote, lock contract.",
    ],
  },
  {
    id: "add-contract",
    title: "How to Add a Contract",
    where: "Contracts module → click 'New contract'.",
    steps: [
      "Click New contract.",
      "Enter title (e.g. 'Riverside Clinic – Phlebotomy SLA').",
      "Pick status: Draft, Pending Signature, Active, Expired, Cancelled.",
      "Link to a Facility or a Consumer client.",
      "Set start & end dates — the calendar will show contract-end reminders automatically.",
      "Optionally attach the signed PDF (max 25 MB).",
      "Click Create.",
    ],
  },
  {
    id: "upload-documents",
    title: "How to Upload Documents",
    where: "Documents (client/business records) OR Operations & Docs (staff credentials).",
    steps: [
      "Documents tab: client-facing records, contracts, invoices, etc.",
      "Operations & Docs: staff certifications, licenses, CPR/BLS — supports expiration tracking.",
      "Both support PDF, JPG, PNG, DOCX up to 25 MB.",
      "Add a display name, category, and notes for searchability.",
    ],
  },
  {
    id: "create-service-request",
    title: "How to Create a Service Request",
    where: "Service requests = consumer leads. Either submit via public form or Manual Entry Center.",
    steps: [
      "Submission lands in Leads with status 'new'.",
      "Review urgency (Standard / Same Day / Immediate).",
      "Update status to 'scheduled' to place it on the Calendar.",
      "Drag the lead through the Dispatch Center lanes as service progresses.",
    ],
  },
  {
    id: "assign-staff",
    title: "How to Assign Staff",
    where: "Dispatch Center → drag a lead to the 'Assigned' lane.",
    steps: [
      "Open Dispatch Center.",
      "Drag the job card from Pending into Assigned (auto-saves on drop).",
      "Continue to En Route → On Site → Completed as the job progresses.",
      "Cards in 'Completed' are auto-removed from the board.",
    ],
  },
  {
    id: "create-invoices",
    title: "How to Create Invoices",
    where: "Open any consumer lead → Billing section in the detail dialog.",
    steps: [
      "Open a lead in the Leads panel.",
      "Use the Billing card to set amount due, pricing type, and a Stripe link.",
      "Click 'Send invoice email' to dispatch a Resend-powered email with the payment link.",
    ],
  },
  {
    id: "record-payments",
    title: "How to Record Payments",
    where: "Lead detail → Billing card.",
    steps: [
      "Open the lead.",
      "Set amount_paid and payment_status (paid / partial / overdue).",
      "Click 'Mark paid' for one-tap full settlement.",
      "Monthly revenue on the Dashboard updates instantly.",
    ],
  },
  {
    id: "process-refunds",
    title: "How to Process Refunds",
    where: "Lead detail → Billing card. Refunds are recorded as a payment_status change + note.",
    steps: [
      "Open the lead and set payment_status to a refund-appropriate value.",
      "Add a timestamped note in the activity log with the refund reason and amount.",
      "Upload any refund documentation under Documents → Financial.",
    ],
  },
  {
    id: "manage-services",
    title: "How to Manage Services",
    where: "Services module — toggle availability.",
    steps: [
      "Open Services.",
      "Toggle a service off (unavailable) → the public form greys it out instantly.",
      "Toggle back on to restore selection on the public form.",
    ],
  },
  {
    id: "reactivate-lost-leads",
    title: "How to Reactivate Lost Leads",
    where: "Leads panel → use the Archived / Bin filters.",
    steps: [
      "Click the Archived chip to view paused leads.",
      "Open any archived lead and click Restore from Archive.",
      "Add a note explaining the reactivation reason.",
      "Update status to continue the workflow.",
    ],
  },
];

export const UNIVERSAL_WORKFLOW = [
  {
    n: 1, title: "Lead Received",
    sources: ["Website Forms", "Phone Calls", "Emails", "Referrals", "Partnerships", "Getlabs", "Manual Entry"],
    actions: ["Create Lead Record", "Assign Lead ID", "Add Notes", "Set Priority"],
    statuses: [],
  },
  {
    n: 2, title: "Lead Review",
    actions: ["Verify Information", "Determine Service Needed", "Review Coverage Area", "Review Staffing Needs"],
    statuses: ["New Inquiry", "Needs Follow-Up", "Priority"],
  },
  {
    n: 3, title: "Qualification",
    actions: ["Verify Service Availability", "Verify Staffing", "Verify Scheduling"],
    statuses: ["Qualified", "Waiting List", "Not Qualified"],
  },
  {
    n: 4, title: "Quote / Proposal",
    actions: ["Create Estimate", "Send Pricing", "Send Service Agreement"],
    statuses: ["Quote Sent", "Awaiting Response"],
  },
  {
    n: 5, title: "Negotiation",
    actions: ["Modify Pricing", "Modify Scope", "Update Proposal"],
    statuses: ["Negotiating"],
  },
  {
    n: 6, title: "Contract / Service Approval",
    actions: ["Upload Signed Documents", "Upload Agreements", "Record Renewal Dates"],
    statuses: ["Contract Pending", "Approved"],
  },
  {
    n: 7, title: "Client Onboarding",
    actions: ["Create Client Profile", "Create Facility Profile", "Upload Required Documents", "Add Service Requirements"],
    statuses: ["Active Client"],
  },
  {
    n: 8, title: "Scheduling & Dispatch",
    actions: ["Create Appointment", "Assign Staff", "Create Route", "Notify Team"],
    statuses: ["Scheduled"],
  },
  {
    n: 9, title: "Service Completion",
    actions: ["Complete Service", "Upload Service Documents", "Record Notes"],
    statuses: ["Completed"],
  },
  {
    n: 10, title: "Billing & Payments",
    actions: ["Create Invoice", "Send Invoice", "Record Payments"],
    statuses: ["Paid", "Partial Payment", "Overdue"],
  },
  {
    n: 11, title: "Follow-Up",
    actions: ["Client Satisfaction Check", "Future Scheduling", "Retention Activities"],
    statuses: ["Retained Client"],
  },
];

export const EXCEPTION_WORKFLOWS = [
  {
    id: "lost-lead",
    title: "Lost Lead",
    reasons: ["No Response", "Competitor Selected", "Pricing", "Service Unavailable"],
    statuses: ["Lost Lead"],
  },
  {
    id: "reactivation",
    title: "Lead Reactivation",
    actions: ["Reopen Lead", "Update Notes", "Continue Workflow"],
    statuses: ["Active Again"],
  },
  {
    id: "cancellation",
    title: "Cancellation",
    actions: ["Record Reason", "Notify Staff", "Close Appointment"],
    statuses: ["Cancelled"],
  },
  {
    id: "refund",
    title: "Refund Request",
    actions: ["Review Request", "Approve or Deny", "Record Refund Amount"],
    statuses: ["Refund Pending", "Refunded", "Denied"],
  },
  {
    id: "lost-contract",
    title: "Lost Contract",
    actions: ["Record Reason", "Archive Contract", "Schedule Future Follow-Up"],
    statuses: ["Lost Contract"],
  },
];

export const COMPANY_SOPS = [
  { id: "phlebotomy", title: "Mobile Phlebotomy Procedure", body: "Standard operating procedure for mobile blood draws — patient identification, supply checklist, draw protocol, chain-of-custody labelling, transport to lab, and post-visit documentation." },
  { id: "client-intake", title: "Client Intake Procedure", body: "How to take a new client from first contact through profile creation: verifying contact info, capturing services needed, recording communication preferences, and creating the initial record." },
  { id: "facility-onboarding", title: "Facility Onboarding Procedure", body: "End-to-end facility partnership onboarding: NDA, services agreement, insurance docs, staff credentials, contract upload, and kickoff scheduling." },
  { id: "dispatch", title: "Dispatch Procedure", body: "How to triage incoming service requests, prioritize by urgency, assign field staff, communicate ETAs, and track jobs through the dispatch board lanes." },
  { id: "doc-mgmt", title: "Document Management Procedure", body: "Where to store each document type — Documents (client/business), Operations & Docs (staff credentials). Naming, categorisation, expiration tracking and access control." },
  { id: "contract-mgmt", title: "Contract Management Procedure", body: "Contract lifecycle: draft, pending signature, active, expired, cancelled. Renewal cadence, who signs, how to upload, and how to surface 30/60/90-day reminders." },
  { id: "billing", title: "Billing Procedure", body: "Quoting, invoicing, sending the Stripe link, marking paid, handling partial payments and overdue accounts. Monthly revenue is auto-aggregated on the Dashboard." },
  { id: "refund", title: "Refund Procedure", body: "Triage a refund request, review the original transaction, approve/deny, record the refund amount and reason, and upload any supporting documentation." },
  { id: "staff-assignment", title: "Staff Assignment Procedure", body: "How to choose the right field staff for a job: skills match, certification status, availability, coverage area, and conflict checking." },
];

// Manual Entry Center cards.
// Each card links to an existing admin module (we navigate the sidebar) or
// points to the public form when admin-side creation isn't applicable.
export const MANUAL_ENTRY_CARDS = [
  { id: "leads", title: "Lead", desc: "Open the Leads panel to view & manage every service request.", nav: "consumer", icon: "Users" },
  { id: "clients", title: "Client", desc: "Unified directory of consumers and facility contacts.", nav: "clients", icon: "UserRound" },
  { id: "facilities", title: "Facility / Provider", desc: "B2B pipeline — facilities & partnership prospects.", nav: "facility", icon: "Building2" },
  { id: "contracts", title: "Contract", desc: "Add a contract record with optional signed PDF.", nav: "contracts", icon: "FileSignature" },
  { id: "services", title: "Service Request", desc: "Open Leads — service requests live in the consumer pipeline.", nav: "consumer", icon: "Stethoscope" },
  { id: "documents", title: "Document", desc: "Upload client / business / financial files.", nav: "documents", icon: "Files" },
  { id: "operations", title: "Staff Credential", desc: "Upload staff certifications, licenses, CPR/BLS.", nav: "operations", icon: "IdCard" },
  { id: "tasks", title: "Task", desc: "Personal reminder or shared to-do.", nav: "tasks", icon: "ClipboardList" },
];
